import type {TuiCountryIsoCode, TuiLanguage} from '@taiga-ui/i18n/types';

// addon-commerce.ts
import type {TuiLanguageCommerce} from '@taiga-ui/i18n/types';

export const TUI_FINNISH_LANGUAGE_ADDON_COMMERCE: TuiLanguageCommerce = {
    cardNumber: ['Numero', 'Korttinumero'],
    cardExpiry: ['Vanheneminen', 'Vanhentumispäivä'],
};

// layout.ts
import type {TuiLanguageLayout} from '@taiga-ui/i18n/types';

export const TUI_FINNISH_LANGUAGE_LAYOUT: TuiLanguageLayout = {
    inputSearch: {
        popular: 'Suosittu',
        history: 'Viimeisin',
        placeholder: 'Etsi',
        hotkey: 'etsimiseen',
        all: 'Kaikki',
        empty: 'Ei tuloksia',
    },
};

// kit.ts
import type {TuiLanguageKit} from '@taiga-ui/i18n/types';

export const TUI_FINNISH_LANGUAGE_KIT: TuiLanguageKit = {
    cancel: 'Peruuta',
    done: 'Valmis',
    more: 'Lisää',
    otherDate: 'Muu päivämäärä...',
    showAll: 'Näytä kaikki',
    hide: 'Piilota',
    mobileCalendarTexts: [
        'Valitse päivä',
        'Valitse ajanjakso',
        'Valitse päivät',
    ],
    range: ['Alkaen', 'Päättyen'],
    countTexts: ['Enemmän', 'Vähemmän'],
    time: {
        'HH:MM': 'HH:MM', 
        'HH:MM AA': 'HH:MM AA', 
        'HH:MM:SS': 'HH:MM:SS',
        'HH:MM:SS AA': 'HH:MM:SS AA',
        'HH:MM:SS.MSS': 'HH:MM:SS.MSS',
        'HH:MM:SS.MSS AA': 'HH:MM:SS.MSS AA',
    },
    dateTexts: {
        DMY: 'päivä.kuukausi.vuosi', 
        MDY: 'kuukausi.päivä.vuosi', 
        YMD: 'vuosi.kuukausi.päivä', 
    },
    digitalInformationUnits: ['Tavu', 'Kilotavu', 'Megatavu'],
    passwordTexts: ['Näytä salasana', 'Piilota salasana'],
    copyTexts: ['Kopioi', 'Kopioitu'],
    shortCalendarMonths: [
        'Tammi',
        'Helmi',
        'Maalis',
        'Huhti',
        'Touko',
        'Kesä',
        'Heinä',
        'Elo',
        'Syys',
        'Loka',
        'Marras',
        'Joulu',
    ],
    pagination: ['Edellinen sivu', 'Seuraava sivu'],
    fileTexts: {
        loadingError: 'Latausvirhe',
        preview: 'Esikatselu',
        remove: 'Poista',
    },
    inputFileTexts: {
        defaultLabelSingle: 'tai pudota se tähän',
        defaultLabelMultiple: 'tai pudota ne tähän',
        defaultLinkSingle: 'Valitse tiedosto',
        defaultLinkMultiple: 'Valitse tiedostot',
        maxSizeRejectionReason: 'Tiedosto on liian suuri',
        formatRejectionReason: 'Virheellinen tiedostomuoto',
        drop: 'Pudota tiedosto tähän',
        dropMultiple: 'Pudota tiedostot tähän',
    },
    multiSelectTexts: {
        all: 'Valitse kaikki',
        none: 'Älä valitse mitään',
    },
    confirm: {
        yes: 'Kyllä',
        no: 'Ei',
    },
    previewTexts: {
        rotate: 'Kierrä',
    },
    zoomTexts: {
        zoomOut: 'Loitonna',
        zoomIn: 'Lähennä',
        reset: 'Palauta',
    },
    phoneSearch: 'Etsi maata tai koodia',
};

// addon-editor.ts
import type {TuiLanguageEditor} from '@taiga-ui/i18n/types';

export const TUI_FINNISH_LANGUAGE_ADDON_EDITOR: TuiLanguageEditor = {
    colorSelectorModeNames: ['Kiinteä väri', 'Värigradientti'],
    toolbarTools: {
        undo: 'Kumoa',
        redo: 'Tee uudelleen',
        font: 'Kirjasin',
        fontStyle: 'Kirjasintyyli',
        fontSize: 'Kirjasinkoko',
        bold: 'Lihavoitu',
        italic: 'Kursivoitu',
        underline: 'Alleviivattu',
        strikeThrough: 'Yliviivattu',
        justify: 'Tasaus',
        justifyLeft: 'Tasaa vasemmalle',
        justifyCenter: 'Keskitä',
        justifyRight: 'Tasaa oikealle',
        justifyFull: 'Tasaa molemmat reunat',
        list: 'Lista',
        indent: 'Suurenna sisennystä',
        outdent: 'Pienennä sisennystä',
        unorderedList: 'Luettelo',
        orderedList: 'Numeroitu lista',
        quote: 'Lainaus',
        foreColor: 'Tekstin väri',
        backColor: 'Taustaväri',
        hiliteColor: 'Korostusväri',
        clear: 'Tyhjennä',
        link: 'Linkki',
        attach: 'Liitä tiedosto',
        tex: 'Lisää TeX',
        code: 'Koodi',
        image: 'Lisää kuva',
        insertHorizontalRule: 'Lisää vaakaviiva',
        superscript: 'Yläindeksi',
        subscript: 'Alaindeksi',
        insertTable: 'Lisää taulukko',
        insertGroup: 'Lisää ryhmä',
        hiliteGroup: 'Korosta ryhmä',
        removeGroup: 'Poista ryhmä',
        insertAnchor: 'Lisää ankkuri',
        mergeCells: 'Yhdistä solut',
        splitCells: 'Jaa solut',
        rowsColumnsManaging: 'Hallitse rivejä ja sarakkeita',
        cellColor: 'Solun väri',
        setDetails: 'Aseta tiedot',
        removeDetails: "Poista tiedot",
    },
    editorEditLink: {
        urlExample: 'esimerkki.com',
        anchorExample: 'ankkuri',
    },
    editorTableCommands: [
        ['Lisää sarake eteen', 'Lisää sarake perään'],
        ['Lisää rivi yläpuolelle', 'Lisää rivi alapuolelle'],
        ['Poista sarake', 'Poista rivi'],
    ],
    editorCodeOptions: ['Koodi tekstissä', 'Koodilohko'],
    editorFontOptions: {
        small: 'Pieni',
        large: 'Suuri',
        normal: 'Normaali',
        title: 'Otsikko',
        subtitle: 'Alaotsikko',
    },
};

// addon-table.ts
import type {TuiLanguageTable} from '@taiga-ui/i18n/types';

export const TUI_FINNISH_LANGUAGE_ADDON_TABLE: TuiLanguageTable = {
    showHideText: 'Näytä/Piilota',
    paginationTexts: {
        pages: 'Sivut',
        linesPerPage: 'Rivejä per sivu',
        of: '/',
    },
};

// core.ts
import type {TuiLanguageCore} from '@taiga-ui/i18n/types';

export const TUI_FINNISH_LANGUAGE_CORE: TuiLanguageCore = {
    months: [
        'Tammikuu',
        'Helmikuu',
        'Maaliskuu',
        'Huhtikuu',
        'Toukokuu',
        'Kesäkuu',
        'Heinäkuu',
        'Elokuu',
        'Syyskuu',
        'Lokakuu',
        'Marraskuu',
        'Joulukuu',
    ],
    close: 'Sulje',
    clear: 'Tyhjennä',
    nothingFoundMessage: 'Ei tuloksia',
    defaultErrorMessage: 'Virheellinen arvo',
    spinTexts: ['Edellinen', 'Seuraava'],
    shortWeekDays: ['Ma', 'Ti', 'Ke', 'To', 'Pe', 'La', 'Su'],
    countries: {} as Record<TuiCountryIsoCode, string>,
};

export const TUI_FINNISH_LANGUAGE: TuiLanguage = {
    name: 'finnish',
    ...TUI_FINNISH_LANGUAGE_CORE,
    ...TUI_FINNISH_LANGUAGE_KIT,
    ...TUI_FINNISH_LANGUAGE_ADDON_TABLE,
    ...TUI_FINNISH_LANGUAGE_ADDON_COMMERCE,
    ...TUI_FINNISH_LANGUAGE_ADDON_EDITOR,
    ...TUI_FINNISH_LANGUAGE_LAYOUT,
};